<template>
  <div
    class="min-h-screen relative overflow-hidden flex items-center justify-center font-NotoSerifKhmer p-4"
  >
    <!-- Animated Background -->
    <div
      class="fixed inset-0 bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 bg-[length:400%_400%] animate-gradient -z-20"
    ></div>
    <div
      class="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_20%_50%,rgba(255,107,157,0.3)_0%,transparent_50%),radial-gradient(circle_at_80%_80%,rgba(102,126,234,0.3)_0%,transparent_50%),radial-gradient(circle_at_40%_20%,rgba(67,233,123,0.2)_0%,transparent_50%)]"
    ></div>

    <!-- Floating Decorative Elements -->
    <div
      class="fixed top-20 left-10 w-72 h-72 bg-pink-400/20 rounded-full blur-3xl animate-pulse"
    ></div>
    <div
      class="fixed bottom-20 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-pulse"
      style="animation-delay: 1s"
    ></div>

    <!-- Language Selector -->
    <div class="fixed top-6 right-6 z-50 px-4 py-2" ref="languageDropdown">
      <div class="relative">
        <button
          @click.stop="toggleLanguageMenu"
          class="flex items-center gap-3 px-3 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-xl rounded-2xl transition-all duration-300 border border-white/20 shadow-lg group"
        >
          <div class="flex items-center gap-2">
            <img
              :src="languages.find((l) => l.code === selectedLang)?.flag"
              class="w-5 h-3.5 rounded-sm object-cover shadow-sm"
            />
            <span class="text-white font-semibold text-sm hidden sm:block">{{
              languages.find((l) => l.code === selectedLang)?.name
            }}</span>
          </div>
          <svg
            class="w-4 h-4 text-white transition-transform duration-300"
            :class="{ 'rotate-180': showLanguageMenu }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <!-- Dropdown Menu -->
        <transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 scale-95 translate-y-2"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-2"
        >
          <div
            v-if="showLanguageMenu"
            class="absolute right-0 mt-3 w-48 bg-white/15 backdrop-blur-2xl rounded-2xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.3)] overflow-hidden"
          >
            <div class="p-2 space-y-1">
              <button
                v-for="lang in languages"
                :key="lang.code"
                @click="onLanguageChange(lang.code)"
                class="w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 group/item"
                :class="
                  selectedLang === lang.code
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                "
              >
                <div class="flex items-center gap-3">
                  <img
                    :src="lang.flag"
                    :alt="lang.name"
                    class="w-5 h-3.5 rounded-sm object-cover shadow-sm"
                  />
                  <span class="text-sm font-medium">{{ lang.name }}</span>
                </div>
                <div
                  v-if="selectedLang === lang.code"
                  class="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.5)]"
                ></div>
              </button>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Register Card -->
    <div class="w-full max-w-md animate-fade-in-up">
      <!-- Logo/Header -->
      <div class="text-center mb-8">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.1)] mb-4 animate-bounce-slow"
        >
          <span class="text-5xl">💍</span>
        </div>
        <h1
          class="text-4xl md:text-5xl font-bold text-white mb-2 drop-shadow-lg"
        >
          {{ $t("Create Account") }}
        </h1>
        <p class="text-white/80 text-lg">{{ $t("Start planning your perfect day") }}</p>
      </div>

      <!-- Register Form Card -->
      <div
        class="bg-white/15 backdrop-blur-xl rounded-3xl border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.1),0_2px_8px_rgba(255,255,255,0.2)_inset] p-8 transition-all duration-300 hover:shadow-[0_12px_48px_rgba(0,0,0,0.15),0_2px_12px_rgba(255,255,255,0.3)_inset]"
      >
        <form @submit.prevent="handleRegister" class="space-y-5">
          <!-- Full Name Input -->
          <div>
            <label
              for="userName"
              class="block text-sm font-semibold text-white/90 mb-2"
              >{{ $t("Username") }}</label
            >
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
              >
                <svg
                  class="w-5 h-5 text-white/60"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <input
                id="userName"
                v-model="username"
                type="text"
                required
                :placeholder="$t('John & Jane Doe')"
                class="w-full bg-white/10 border border-white/20 rounded-2xl py-3.5 pl-12 pr-4 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
              />
                    <span
                    v-if="errorCheckUserName"
                    class="text-red-400 text-sm mt-1"
                    >{{ errorCheckUserName }}</span
                  >
            </div>
          </div>

          <!-- Email Input -->
          <div>
            <label
              for="email"
              class="block text-sm font-semibold text-white/90 mb-2"
              >{{ $t("Email") }}</label
            >
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
              >
                <svg
                  class="w-5 h-5 text-white/60"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
              </div>
              <input
                id="email"
                v-model="email"
                type="email"
                required
                :placeholder="$t('email_placeholder')"
                class="w-full bg-white/10 border border-white/20 rounded-2xl py-3.5 pl-12 pr-4 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
              />
                <span
                    v-if="errorCheckEmail"
                    class="text-red-400 text-sm mt-1"
                    >{{ errorCheckEmail }}</span
                  >
            </div>
          </div>

          <!-- Password Input -->
          <div>
            <label
              for="password"
              class="block text-sm font-semibold text-white/90 mb-2"
              >{{ $t("Password") }}</label
            >
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
              >
                <svg
                  class="w-5 h-5 text-white/60"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                required
                :placeholder="$t('Create a strong password')"
                class="w-full bg-white/10 border border-white/20 rounded-2xl py-3.5 pl-12 pr-12 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-4 flex items-center text-white/60 hover:text-white transition-colors"
              >
                <svg
                  v-if="!showPassword"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                <svg
                  v-else
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              </button>
            </div>
            <!-- Password Strength Indicator -->
            <div v-if="password" class="mt-2">
              <div class="flex gap-1 mb-1">
                <div
                  class="h-1 flex-1 rounded-full transition-all duration-300"
                  :class="passwordStrength >= 1 ? 'bg-red-400' : 'bg-white/20'"
                ></div>
                <div
                  class="h-1 flex-1 rounded-full transition-all duration-300"
                  :class="
                    passwordStrength >= 2 ? 'bg-yellow-400' : 'bg-white/20'
                  "
                ></div>
                <div
                  class="h-1 flex-1 rounded-full transition-all duration-300"
                  :class="
                    passwordStrength >= 3 ? 'bg-emerald-400' : 'bg-white/20'
                  "
                ></div>
              </div>
              <p class="text-xs text-white/70">
                <span v-if="passwordStrength === 1">{{ $t("Weak password") }}</span>
                <span v-else-if="passwordStrength === 2">{{ $t("Medium password") }}</span>
                <span v-else-if="passwordStrength === 3">{{ $t("Strong password") }}</span>
              </p>
            </div>
          </div>

          <!-- Confirm Password Input -->
          <div>
            <label
              for="confirmPassword"
              class="block text-sm font-semibold text-white/90 mb-2"
              >{{ $t("Confirm Password") }}</label
            >
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
              >
                <svg
                  class="w-5 h-5 text-white/60"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                required
                :placeholder="$t('Confirm your password')"
                class="w-full bg-white/10 border border-white/20 rounded-2xl py-3.5 pl-12 pr-12 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-300"
                :class="{
                  'border-red-400':
                    confirmPassword && password !== confirmPassword,
                }"
              />
              <button
                type="button"
                @click="showConfirmPassword = !showConfirmPassword"
                class="absolute inset-y-0 right-0 pr-4 flex items-center text-white/60 hover:text-white transition-colors"
              >
                <svg
                  v-if="!showConfirmPassword"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                <svg
                  v-else
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              </button>
            </div>
            <p
              v-if="confirmPassword && password !== confirmPassword"
              class="text-xs text-red-400 mt-1"
            >
              {{ $t("Passwords do not match") }}
            </p>
          </div>

          <!-- Terms & Conditions -->
          <!-- <div>
            <label class="flex items-start cursor-pointer group">
              <input
                v-model="acceptTerms"
                type="checkbox"
                required
                class="w-5 h-5 mt-0.5 bg-white/10 border-2 border-white/30 rounded-lg text-purple-600 focus:ring-2 focus:ring-white/40 transition-all cursor-pointer flex-shrink-0"
              />
              <span
                class="ml-3 text-sm text-white/80 group-hover:text-white transition-colors"
              >
                I agree to the
                <a href="#" class="text-white font-semibold underline"
                  >Terms & Conditions</a
                >
                and
                <a href="#" class="text-white font-semibold underline"
                  >Privacy Policy</a
                >
              </span>
            </label>
          </div> -->

          <!-- Register Button -->
          <button
            type="submit"
            :disabled="!isFormValid"
            class="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-4 rounded-2xl shadow-[0_8px_24px_rgba(168,85,247,0.4)] hover:shadow-[0_12px_32px_rgba(168,85,247,0.5)] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            <span
              v-if="!isLoading"
              class="flex items-center justify-center gap-2"
            >
              <span>{{ $t("Create Account") }}</span>
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </span>
            <span v-else class="flex items-center justify-center gap-2">
              <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>{{ $t("Creating account...") }}</span>
            </span>
          </button>
        </form>

        <div class="mt-4" v-if="errorMessage">
          <span class="text-red-400 text-sm">{{ errorMessage }}</span>
        </div>

        <!-- Divider -->
        <div class="relative my-8">
          <div class="absolute inset-0 flex items-center">
            <div class="w-full border-t border-white/20"></div>
          </div>
          <div class="relative flex justify-center text-sm">
            <span
              class="px-4 bg-white/10 backdrop-blur-sm text-white/70 rounded-full"
              >{{ $t("Or continue with") }}</span
            >
          </div>
        </div>

        <!-- Social Register Buttons -->
        <div class="grid grid-cols-2 gap-4">
          <button
            @click="handleSignupWithGoogle"
            type="button"
            class="flex col-span-2 items-center justify-center gap-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-2xl py-3 px-4 text-white font-semibold transition-all duration-300 hover:-translate-y-0.5"
          >
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            <span>Google</span>
          </button>
          <!-- <button
            @click="handleSignupWithFacebook"
            type="button"
            class="flex items-center justify-center gap-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-2xl py-3 px-4 text-white font-semibold transition-all duration-300 hover:-translate-y-0.5"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
              />
            </svg>
            <span>Facebook</span>
          </button> -->
        </div>
      </div>

      <!-- Sign In Link -->
      <div class="text-center mt-6">
        <div class="text-white/80 flex space-x-2 justify-center">
          <span> {{ $t("Already have an account?") }} </span>
          <div
            @click="goToLogin"
            class="text-white font-bold hover:underline ml-1 cursor-pointer"
          >
            {{ $t("Sign in") }}
          </div>
        </div>
      </div>
    </div>

    <!-- Social Login Loading Overlay -->
    <transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="isSocialLoading"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-2xl bg-white/10"
      >
        <div
          class="bg-white/20 backdrop-blur-3xl border border-white/30 rounded-3xl p-8 shadow-2xl flex flex-col items-center gap-6 animate-fade-in-up"
        >
          <div class="relative">
            <div class="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
            <div class="absolute inset-0 flex items-center justify-center">
              <span class="text-2xl animate-pulse">💍</span>
            </div>
          </div>
          <div class="text-center">
            <h3 class="text-xl font-bold text-white mb-1">{{ $t("Authenticating...") }}</h3>
            <p class="text-white/60 text-sm">{{ $t("Please wait while we secure your connection") }}</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
    <component
    :is="currentComponent"
    :visible="openForm"
    :isFailed="isFailed"
    :informMessage="informMessage"
    :showToast="showToast"
    @onClose="handleListenEmitEvent"
  />
</template>

<script setup>
import { useSignup } from "@/composables/useSignup";
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { v4 as uuidv4 } from "uuid";
import khFlag from "@/assets/Cambodia.png";
import usaFlag from "@/assets/English.png";
import { useRouter } from "vue-router";
import { getCollectionQuery } from "@/composables/getCollection";
import { serverTimestamp, where } from "firebase/firestore";
import useCollection from "@/composables/useDocument";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase/config";
import ToastedVue from "@/components/ToastedVue.vue";

const router = useRouter();
const { t, locale } = useI18n();

// Language Selection Logic
const languages = [
  { code: "en", name: "English", flag: usaFlag },
  { code: "km", name: "Khmer", flag: khFlag },
];
const selectedLang = ref(locale.value);
const showLanguageMenu = ref(false);
const languageDropdown = ref(null);

const onLanguageChange = (code) => {
  locale.value = code;
  selectedLang.value = code;
  localStorage.setItem("locale", code);
  showLanguageMenu.value = false;
};

const toggleLanguageMenu = () => {
  showLanguageMenu.value = !showLanguageMenu.value;
};

const handleClickOutside = (event) => {
  if (languageDropdown.value && !languageDropdown.value.contains(event.target)) {
    showLanguageMenu.value = false;
  }
};

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

const goToLogin = () => {
  router.push("/login");
};

// Form data
const username = ref(null);
const email = ref(null);
const weddingDate = ref(null);
const password = ref(null);
const confirmPassword = ref(null);
const acceptTerms = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const isLoading = ref(false);
const showToast = ref(false);
const currentComponent = ref(null);
const informMessage = ref("");
const openForm = ref(false);
const timeStamp=ref(serverTimestamp());

const isSocialLoading = ref(false);
const optionUser = ref([]);
const errorMessage = ref("");

const showErrorMessage = (message) => {
  errorMessage.value = message;
  console.log("message", message);
};

const isFailed = ref(false);

// Password strength calculation
const passwordStrength = computed(() => {
  const pwd = password.value;
  if (!pwd) return 0;

  let strength = 0;
  if (pwd.length >= 8) strength++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength++;
  if (/\d/.test(pwd) && /[!@#$%^&*]/.test(pwd)) strength++;

  return strength;
});
const freePlanData = ref([]);
const fetchFreePlan = async () => {
  await getCollectionQuery(
    "plans",
    [where("planType", "==", "free")],
    (data) => {
      freePlanData.value = data;

    
    },
    null,
    true
  );
};

onMounted(() => {
  fetchFreePlan();
  document.addEventListener("click", handleClickOutside);
});

    const errorCheckUserName = ref(null);
    const handleCheckUserExistenceByUserName = async () => {
      try {
        const datacheckusernameExists = ref([]);

        await getCollectionQuery(
          "users",
          [where("userName", "==", username.value)],
          (data) => {
            datacheckusernameExists.value = data;
          },
          null,
          false
        );

        console.log("datacheckusernameExists", datacheckusernameExists.value);

        if (datacheckusernameExists.value.length > 0) {
          errorCheckUserName.value = t("Username already exists!");
        } else {
          errorCheckUserName.value = "";
        }
      } catch (err) {
        console.log("failed to check  username exist", err);
      }
    };

    const errorCheckEmail = ref(null);
    const handleCheckUserExistenceByEmail = async () => {
      try {
        let response = [];
        await getCollectionQuery(
          "users",
          [where("email", "==", email.value)],
          (data) => {
            response = data;
          },
          null,
          false
        );

        if (response.length > 0) {
          errorCheckEmail.value = t("Email already exists");
        } else {
          errorCheckEmail.value = "";
        }
      } catch (err) {
        console.error("Error checking user existence:", err);
      }
    };

    watch(email, (newVal) => {
      if(newVal){
        handleCheckUserExistenceByEmail();
      }
    });

    watch(username, (newVal) => {
      if(newVal){
        console.log("newVal", newVal);
        handleCheckUserExistenceByUserName();
      }
    });

const handleListenEmitEvent = (status) => {
  if (status == "success") {
    informMessage.value = t("You have been registered successfully !");
    showToast.value = true;
    isFailed.value = false;
    openForm.value = false;

    currentComponent.value = ToastedVue;
    showToast.value = true;
  } else if (status == "close") {
    console.log("part close");
    showToast.value = false;
    isFailed.value = false;
    openForm.value = false;
    currentComponent.value = "";
  } else {
    showToast.value = false;
    isFailed.value = false;
    openForm.value = false;

    currentComponent.value = "";
  }
};

const { signupWithGoogle, signupWithFacebook } = useSignup();

const handleSignupWithGoogle = async () => {
  try {
    isSocialLoading.value = true;
    let userQueryPromise;

    const user = await signupWithGoogle();
    if (user) {
      userQueryPromise = getCollectionQuery(
        "users",
        [where("email", "==", user.email), where("provider", "==", "google")],
        (data) => {
          optionUser.value = data;
        },
        null,
        false
      );
      
      await Promise.all([userQueryPromise]);

      if (optionUser.value.length === 0) {
        showErrorMessage(t("Your Email or Password is incorrect."));
        isSocialLoading.value = false;
        return;
      }

      if (optionUser.value.length > 0) {
        if (optionUser.value[0].status === false && optionUser.value[0].role === "User") {
          showErrorMessage(t("Your account is disabled."));
          isSocialLoading.value = false;
          return;
        }

        if (optionUser.value[0].role === "User") {
          router.push("/");
        } else {
          router.push("/admin");
        }
      }
    }
    isSocialLoading.value = false;
  } catch (err) {
    console.log("failed to submit data", err);
    isSocialLoading.value = false;
  }
};

const handleSignupWithFacebook = async () => {
  try {
    isSocialLoading.value = true;
    let userQueryPromise;

    const user = await signupWithFacebook();
    if (user) {
      userQueryPromise = getCollectionQuery(
        "users",
        [where("email", "==", user.email), where("provider", "==", "facebook")],
        (data) => {
          optionUser.value = data;
        },
        null,
        false
      );

      await Promise.all([userQueryPromise]);

      if (optionUser.value.length === 0) {
        showErrorMessage(t("Your Email or Password is incorrect."));
        isSocialLoading.value = false;
        return;
      }

      if (optionUser.value.length > 0) {
        if (optionUser.value[0].status === false && optionUser.value[0].role === "User") {
          showErrorMessage(t("Your account is disabled."));
          isSocialLoading.value = false;
          return;
        }

        if (optionUser.value[0].role === "User") {
          router.push("/");
        } else {
          router.push("/admin");
        }
      }
    }
    isSocialLoading.value = false;
  } catch (err) {
    console.log("failed to submit data", err);
    isSocialLoading.value = false;
  }
};

// Form validation
const isFormValid = computed(() => {
  return (
    username.value &&
    email.value &&
 
    password.value &&
    confirmPassword.value &&
    password.value === confirmPassword.value &&

    passwordStrength.value >= 2
  );
});

    const { setDocs: creatUser } =
useCollection("users");
// Handle registration
const handleRegister = async () => {
  if (!isFormValid.value) return;

  if(errorCheckUserName.value || errorCheckEmail.value){
    return;
  }

  isLoading.value = true;

  const uid = uuidv4(); // Generate a UUID

  const dataUser = {
    userName: username.value,
    userNameLowerCase: username.value.toLowerCase(),
    displayName: username.value,
    password: password.value,
    provider: "email-password",
    planId: freePlanData.value[0].id,
    email: email.value,
    photoURL: "",
    role: "User",
    status: true,
    createdAt: timeStamp.value,
    createdBy: "Self Created",
    subscriptionStart: timeStamp.value,
  };

  const createUserFunction = httpsCallable(functions, "createUserV2");
  const signupPromise = createUserFunction({
    email: email.value,
    password: password.value,
    uid,
  });
  const setDocsPromise = creatUser(uid, dataUser);

  const [signupResponse, setDocsResponse] = await Promise.all([
    signupPromise,
    setDocsPromise,
  ]);

 

    informMessage.value = t("You have been registered successfully !");
    showToast.value = true;
    isFailed.value = false;
    openForm.value = true;

    currentComponent.value = ToastedVue;

  // Simulate API call
  setTimeout(() => {

    isLoading.value = false;
      router.push("/login");
    // Add your registration logic here
  }, 1500);
};
</script>

<style>
/* Custom animations for Tailwind */
@keyframes gradient {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounce-slow {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@tailwind utilities;

@layer utilities {
  .animate-gradient {
    animation: gradient 15s ease infinite;
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.6s ease-out;
  }

  .animate-bounce-slow {
    animation: bounce-slow 2s ease-in-out infinite;
  }
}
</style>